import React, { useCallback, useEffect, useState } from 'react'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import IamSessionCommandLog from '@devnw/cyber-iam/iam/SessionCommandLog/SessionCommandLog'
import type { SessionCommandLogEntry as IamSessionCommandLogEntry } from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../../api/genClient'
import {
  toIamSessionCommandLogEntry,
  type WireSessionCommandLogEntry,
} from '../../api/brokeredSessionMapping'

interface CommandLogViewProps {
  tenantId: string
  sessionId: string
}

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Load failed'
}

/**
 * OIDCPAM Gate-6 Phase 5c: migrated off MUI onto
 * @devnw/cyber-iam/iam/SessionCommandLog — the exact parity component built
 * from this view. The component owns the "Load more" affordance and the
 * `[filtered]`-command masking (it never renders `entry.command` when
 * `entry.filtered` is true, even if this page passed it); this page still
 * owns the API call, the CURSOR PAGINATION state (holds `nextCursor`, appends
 * each page's mapped entries), and the wire-mapping (../../api/
 * brokeredSessionMapping — fixing a latent camelCase/snake_case bug, see
 * that file's header: seq_num/entry_type/recorded_at rendered blank in
 * production pre-migration).
 */
export const CommandLogView = ({
  tenantId,
  sessionId,
}: CommandLogViewProps): JSX.Element => {
  const client = useAdminGenClient()
  const [entries, setEntries] = useState<IamSessionCommandLogEntry[]>([])
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadPage = useCallback(
    (cursor?: string): void => {
      setLoading(true)
      setError(null)
      client
        .adminGetBrokeredSessionRecordings({
          tenantId,
          sessionId,
          cursor: cursor || undefined,
          limit: 50,
        })
        .then((result) => {
          const page = (
            (result.entries ?? []) as unknown as WireSessionCommandLogEntry[]
          ).map(toIamSessionCommandLogEntry)
          if (cursor) {
            setEntries((prev) => [...prev, ...page])
          } else {
            setEntries(page)
          }
          setNextCursor(result.nextCursor || undefined)
        })
        .catch((e: unknown) => setError(errMessage(e)))
        .finally(() => setLoading(false))
    },
    [client, tenantId, sessionId]
  )

  useEffect(() => {
    loadPage(undefined)
  }, [loadPage])

  const handleLoadMore = (): void => {
    if (nextCursor) loadPage(nextCursor)
  }

  return (
    <IamSessionCommandLog
      testID="brokered-session-command-log"
      entries={entries}
      hasMore={nextCursor != null}
      onLoadMore={handleLoadMore}
      loading={loading}
      error={error ?? undefined}
    />
  )
}
